import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {staffGetAll, staffExcel} from '../../../redux/slices/staff.slice'
import {projectLookup} from '../../../redux/slices/lookup.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectStaffData = (state) => ({
  staff: state.staff.staff,
  loading: state.staff.loading,
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
})

function List() {
  const intl = useIntl()
  const {staff, loading} = useSelector(selectStaffData)
  const {projectLookupList} = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'STAFF'

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(staffGetAll(params))
  }, [params, dispatch])

  useEffect(() => {
    dispatch(projectLookup())
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      staffExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.STAFF'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'STAFF.SEARCH.NAMESURNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'nameSurname',
        Cell: ({...props}) => <CustomCellDouble item={props} double='title' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'STAFF.SEARCH.PHONE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'phone',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'STAFF.SEARCH.TOTALBALANCE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'balance',
        Cell: ({...props}) => <CustomCellDouble item={props} double='balanceInformation' price />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'STAFF.SEARCH.PROJECT',
            data: projectLookupList,
            name: 'projectId',
          },
          {
            title: 'STAFF.CREATE.IDENTITYNUMBER',
            type: 'text',
            name: 'identityNumber',
          },
          {
            title: 'FILTER.HASBALANCE',
            data: [
              {
                id: 'true',
                name: intl.formatMessage({id: 'FILTER.HASBALANCE.TRUE'}),
              },
              {
                id: 'false',
                name: intl.formatMessage({id: 'FILTER.HASBALANCE.FALSE'}),
              },
            ],
            name: 'hasBalance',
          },
        ],
      },
    ],
    [handleExcelExport, projectLookupList, intl]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.STAFF'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!staff && <ListLoading />}
          {staff && (
            <Table
              items={staff}
              cols={columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
